/**
 * OrgSwitcher — current-org dropdown for the admin app bar (issue #275 —
 * multi-tenancy Phase 4).
 *
 * Renders a small button labelled with the active org's name; clicking
 * opens a menu of every org the user belongs to. Picking another org
 * fires `POST /api/admin/me/current-org`, invalidates the cross-org
 * query caches, and re-renders the page against the new org.
 *
 * Visibility:
 *   - Hidden while `useMyOrgs` is loading (no flash of empty switcher).
 *   - Hidden when the user has zero memberships (a defence-in-depth
 *     state — every user has at least their personal org post-#272).
 *   - Renders even when the user has exactly one org: the dropdown
 *     just lists that one option, plus an "Organization details" link
 *     so the actor can rename / manage members.
 *
 * testIDs (per issue spec):
 *   - `org-switcher`                       — the trigger button
 *   - `org-switcher-option-<id>`           — each menu item
 */

import { useState, type MouseEvent, type ReactElement } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SettingsIcon from "@mui/icons-material/Settings";
import { Link } from "@tanstack/react-router";
import { useMyOrgs, useSetCurrentOrg } from "../hooks/useOrgs";
import { useToast } from "./ToastProvider";

export function OrgSwitcher(): ReactElement | null {
  const orgs = useMyOrgs();
  const switchOrg = useSetCurrentOrg();
  const toast = useToast();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  if (orgs.isPending) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        sx={{ mr: 2, color: "text.secondary" }}
        data-testid="org-switcher-loading"
      >
        <CircularProgress size={16} />
      </Stack>
    );
  }

  // Defence-in-depth: a zero-orgs user can't operate on anything.
  // The auth-required pipe would normally redirect them out; we render
  // nothing here rather than a broken dropdown.
  if (orgs.isError || !orgs.data || orgs.data.orgs.length === 0) {
    return null;
  }

  const current =
    orgs.data.orgs.find((o) => o.id === orgs.data.currentOrganizationId) ?? orgs.data.orgs[0]!;

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handlePick = (orgId: string) => {
    handleClose();
    if (orgId === current.id) return; // No-op switch — don't burn a request.
    switchOrg.mutate(
      { organizationId: orgId },
      {
        onSuccess: () => {
          toast.showSuccess("Switched organization.");
        },
        onError: (err) => {
          toast.showError(err.message || "Could not switch organization.");
        },
      },
    );
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mr: 2, display: { xs: "none", sm: "flex" } }}
      >
        <Chip
          icon={<BusinessIcon />}
          label={`Acting as: ${current.name}`}
          size="small"
          variant="outlined"
          color="primary"
          data-testid="org-switcher-current-chip"
        />
      </Stack>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<BusinessIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleOpen}
        disabled={switchOrg.isPending}
        data-testid="org-switcher"
        aria-haspopup="menu"
        aria-controls={open ? "org-switcher-menu" : undefined}
        aria-expanded={open || undefined}
        sx={{ mr: 1 }}
      >
        {current.name}
      </Button>
      <Menu
        id="org-switcher-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ list: { dense: true } }}
      >
        {orgs.data.orgs.map((org) => {
          const isCurrent = org.id === current.id;
          return (
            <MenuItem
              key={org.id}
              onClick={() => handlePick(org.id)}
              selected={isCurrent}
              data-testid={`org-switcher-option-${org.id}`}
            >
              <ListItemText primary={org.name} secondary={`${org.role} · ${org.slug}`} />
              {isCurrent ? (
                <CheckIcon
                  fontSize="small"
                  sx={{ ml: 1, color: "primary.main" }}
                  aria-label="Current organization"
                />
              ) : null}
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          component={Link}
          to={`/admin/organizations/${current.id}`}
          onClick={handleClose}
          data-testid="org-switcher-manage-link"
        >
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          <ListItemText primary="Organization settings" />
        </MenuItem>
      </Menu>
    </>
  );
}
